/**
 * THE CONVERSATION AS A MESSENGER SHOWS IT, for the chat box and the mind's
 * thread alike: consecutive messages from one side grouped (the first and
 * last of each group marked, for the bubbles' corners and gaps), system
 * events between them, and a time line where the conversation picks up again
 * after a while. Lines the page sent (`context`) are not part of it.
 */
import type { CogentMessage } from "./chat-view";
type ThreadItem =
  | {
      kind: "time";
      key: string;
      label: string;
    }
  | {
      kind: "event";
      key: string;
      text: string;
    }
  | {
      kind: "message";
      key: string;
      message: CogentMessage;
      first: boolean;
      last: boolean;
    };
/** A pause this long between two messages shows the time again. */
const pauseMs = 30 * 60e3;
/** "10:42" today; "Mon 10:42" this week; "Oct 3, 10:42" before. */
export function clock({
  at,
  now = Date.now(),
}: {
  at: string;
  now?: number;
}): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const today = new Date(now);

  if (d.toDateString() === today.toDateString()) return time;

  if (now - d.getTime() < 6 * 86400e3)
    return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;

  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}
export function threadOf(messages: CogentMessage[]): ThreadItem[] {
  const shown = messages.filter((m) => !m.context);
  const out: ThreadItem[] = [];
  let lastAt: number | null = null;

  for (const m of shown) {
    const at = m.at ? Date.parse(m.at) : null;

    if (at !== null && (lastAt === null || at - lastAt > pauseMs))
      out.push({
        kind: "time",
        key: `time:${m.id}`,
        label: clock({
          at: m.at!,
        }),
      });

    if (at !== null) lastAt = at;

    if (m.from === "event") {
      out.push({ kind: "event", key: m.id, text: m.text });
      continue;
    }

    const prev = out.at(-1);
    const first = !(prev?.kind === "message" && prev.message.from === m.from);

    if (!first && prev?.kind === "message") prev.last = false;

    out.push({ kind: "message", key: m.id, message: m, first, last: true });
  }

  return out;
}
